import { useEffect, useId, useRef, useState, type RefObject } from 'react';
import type {
  CommunityCityOption,
  CommunityLifestyleFilter,
  CommunityListFilters,
} from './propertyUtils';
import {
  COMMUNITY_LIFESTYLE_OPTIONS,
  FILTER_PRICE_BOUNDS,
  FILTER_SQFT_BOUNDS,
  formatCityWithState,
} from './propertyUtils';

export const BED_OPTIONS = [
  { value: 0, label: 'Studio' },
  { value: 1, label: '1 Bedroom' },
  { value: 2, label: '2 Bedroom' },
  { value: 3, label: '3 Bedroom' },
  { value: 4, label: '4 Bedroom' },
] as const;

export const BATH_OPTIONS = [
  { value: 1, label: '1 Bath' },
  { value: 2, label: '2 Bath' },
  { value: 3, label: '3 Bath' },
] as const;

interface CommunitiesFiltersProps {
  filters: CommunityListFilters;
  states: { slug: string; name: string }[];
  cities?: CommunityCityOption[];
  resultCount?: number;
  onChange: (next: CommunityListFilters) => void;
  onReset: () => void;
  className?: string;
  /** Sheet layout: no card chrome or duplicate header. */
  embedded?: boolean;
}

export function formatPrice(value: number) {
  if (value >= FILTER_PRICE_BOUNDS.max) {
    return `$${FILTER_PRICE_BOUNDS.max.toLocaleString()}+`;
  }
  return `$${value.toLocaleString()}`;
}

export function formatSqft(value: number) {
  if (value >= FILTER_SQFT_BOUNDS.max) {
    return `${FILTER_SQFT_BOUNDS.max.toLocaleString()}+ sqft`;
  }
  return `${value.toLocaleString()} sqft`;
}

function toggleNumber(list: number[], value: number): number[] {
  return list.includes(value) ? list.filter((item) => item !== value) : [...list, value];
}

function toggleState(list: string[], value: string): string[] {
  return list.includes(value) ? list.filter((item) => item !== value) : [...list, value];
}

function toggleLifestyle(
  list: CommunityLifestyleFilter[],
  value: CommunityLifestyleFilter,
): CommunityLifestyleFilter[] {
  return list.includes(value) ? list.filter((item) => item !== value) : [...list, value];
}

function toggleCity(list: string[], value: string): string[] {
  return list.includes(value) ? list.filter((item) => item !== value) : [...list, value];
}

export const RANGE_SLIDER_CSS = `
  .communities-range {
    pointer-events: none;
  }
  .communities-range::-webkit-slider-thumb {
    pointer-events: auto;
    -webkit-appearance: none;
    appearance: none;
    width: 18px;
    height: 18px;
    border-radius: 9999px;
    background: rgb(var(--ala-secondary));
    border: 2px solid #ffffff;
    box-shadow: 0 1px 3px rgb(var(--ala-primary)/0.25);
    cursor: pointer;
  }
  .communities-range::-moz-range-thumb {
    pointer-events: auto;
    width: 18px;
    height: 18px;
    border-radius: 9999px;
    background: rgb(var(--ala-secondary));
    border: 2px solid #ffffff;
    box-shadow: 0 1px 3px rgb(var(--ala-primary)/0.25);
    cursor: pointer;
  }
  .communities-range::-webkit-slider-runnable-track {
    background: transparent;
  }
  .communities-range::-moz-range-track {
    background: transparent;
  }
`;

function CheckIcon() {
  return (
    <svg viewBox="0 0 16 16" className="h-3.5 w-3.5 shrink-0" fill="none" aria-hidden>
      <path
        d="M3.5 8.5 6.5 11.5 12.5 4.5"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function FiltersIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M4 7h10M18 7h2M4 17h2M10 17h10" />
      <circle cx="16" cy="7" r="2.5" />
      <circle cx="8" cy="17" r="2.5" />
    </svg>
  );
}

function ResetIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M3 12a9 9 0 1 0 3-6.7" />
      <path d="M3 4v5h5" />
    </svg>
  );
}

export function RangeSlider({
  min,
  max,
  valueMin,
  valueMax,
  step,
  onMinChange,
  onMaxChange,
  ariaLabelMin,
  ariaLabelMax,
}: {
  min: number;
  max: number;
  valueMin: number;
  valueMax: number;
  step: number;
  onMinChange: (value: number) => void;
  onMaxChange: (value: number) => void;
  ariaLabelMin: string;
  ariaLabelMax: string;
}) {
  const span = max - min || 1;
  const left = ((valueMin - min) / span) * 100;
  const right = ((valueMax - min) / span) * 100;

  return (
    <div className="relative h-7">
      <div className="absolute left-0 right-0 top-1/2 h-1.5 -translate-y-1/2 rounded-full bg-[rgb(var(--ala-border))]" />
      <div
        className="absolute top-1/2 h-1.5 -translate-y-1/2 rounded-full bg-[rgb(var(--ala-secondary))]"
        style={{ left: `${left}%`, right: `${100 - right}%` }}
      />
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={valueMin}
        aria-label={ariaLabelMin}
        onChange={(event) => {
          const next = Number(event.target.value);
          onMinChange(Math.min(next, valueMax));
        }}
        className="communities-range absolute inset-0 z-[2] w-full appearance-none bg-transparent"
      />
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={valueMax}
        aria-label={ariaLabelMax}
        onChange={(event) => {
          const next = Number(event.target.value);
          onMaxChange(Math.max(next, valueMin));
        }}
        className="communities-range absolute inset-0 z-[3] w-full appearance-none bg-transparent"
      />
    </div>
  );
}

function LifestyleChip({
  active,
  label,
  onClick,
}: {
  active: boolean;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={`inline-flex min-h-10 items-center gap-1.5 rounded-md border px-3 py-2 font-body text-sm transition ${
        active
          ? 'border-[rgb(var(--ala-secondary))] bg-[rgb(var(--ala-secondary))] text-[rgb(var(--ala-on-primary))]'
          : 'border-[rgb(var(--ala-border))] bg-[rgb(var(--ala-background))] text-[rgb(var(--ala-text-muted))] hover:border-[rgb(var(--ala-text))]/30 hover:text-[rgb(var(--ala-text))]'
      }`}
    >
      {active ? <CheckIcon /> : null}
      <span>{label}</span>
    </button>
  );
}

function ChevronIcon({ open }: { open: boolean }) {
  return (
    <svg
      viewBox="0 0 20 20"
      className={`h-4 w-4 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      aria-hidden
    >
      <path d="M5 7.5 10 12.5 15 7.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function useDropdownDismiss(
  open: boolean,
  setOpen: (open: boolean) => void,
  rootRef: RefObject<HTMLDivElement | null>,
) {
  useEffect(() => {
    if (!open) return;

    const onPointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, setOpen, rootRef]);
}

function FilterDropdownTrigger({
  open,
  listId,
  summary,
  hasSelection,
  onToggle,
}: {
  open: boolean;
  listId: string;
  summary: string;
  hasSelection: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      aria-expanded={open}
      aria-controls={listId}
      onClick={onToggle}
      className="flex min-h-11 w-full items-center justify-between gap-3 rounded-md border border-[rgb(var(--ala-border))] bg-[rgb(var(--ala-background))] px-3 py-2 text-left transition hover:border-[rgb(var(--ala-text))]/30"
    >
      <span
        className={`truncate font-body text-sm ${
          hasSelection
            ? 'font-medium text-[rgb(var(--ala-text))]'
            : 'text-[rgb(var(--ala-text-muted))]'
        }`}
      >
        {summary}
      </span>
      <ChevronIcon open={open} />
    </button>
  );
}

function LifestyleDropdown({
  selected,
  onChange,
}: {
  selected: CommunityLifestyleFilter[];
  onChange: (next: CommunityLifestyleFilter[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const listId = useId();
  const selectedCount = selected.length;
  const summary =
    selectedCount === 0
      ? 'Select what matters'
      : selectedCount === 1
        ? COMMUNITY_LIFESTYLE_OPTIONS.find((option) => option.id === selected[0])?.label ||
          '1 selected'
        : `${selectedCount} selected`;

  useDropdownDismiss(open, setOpen, rootRef);

  return (
    <div ref={rootRef} className="relative">
      <FilterDropdownTrigger
        open={open}
        listId={listId}
        summary={summary}
        hasSelection={selectedCount > 0}
        onToggle={() => setOpen((value) => !value)}
      />

      {open ? (
        <div
          id={listId}
          role="group"
          aria-label="What matters to you?"
          className="mt-2 rounded-md border border-[rgb(var(--ala-border))] bg-[rgb(var(--ala-background))] p-3 shadow-[0_8px_24px_rgb(var(--ala-primary)/0.08)]"
        >
          <div className="flex flex-wrap gap-2">
            {COMMUNITY_LIFESTYLE_OPTIONS.map((option) => (
              <LifestyleChip
                key={option.id}
                label={option.label}
                active={selected.includes(option.id)}
                onClick={() => onChange(toggleLifestyle(selected, option.id))}
              />
            ))}
          </div>
        </div>
      ) : null}

      {!open && selectedCount > 0 ? (
        <div className="mt-2 flex flex-wrap gap-2">
          {COMMUNITY_LIFESTYLE_OPTIONS.filter((option) => selected.includes(option.id)).map(
            (option) => (
              <LifestyleChip
                key={option.id}
                label={option.label}
                active
                onClick={() => onChange(toggleLifestyle(selected, option.id))}
              />
            ),
          )}
        </div>
      ) : null}
    </div>
  );
}

function CityDropdown({
  cities,
  selected,
  onChange,
}: {
  cities: CommunityCityOption[];
  selected: string[];
  onChange: (next: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const listId = useId();
  const selectedCount = selected.length;
  const selectedCity = selectedCount === 1 ? cities.find((city) => city.slug === selected[0]) : null;
  const summary =
    selectedCount === 0
      ? 'All cities'
      : selectedCity
        ? formatCityWithState(selectedCity.city, selectedCity.state)
        : selectedCount === 1
          ? '1 city'
          : `${selectedCount} cities`;

  useDropdownDismiss(open, setOpen, rootRef);

  return (
    <div ref={rootRef} className="relative">
      <FilterDropdownTrigger
        open={open}
        listId={listId}
        summary={summary}
        hasSelection={selectedCount > 0}
        onToggle={() => setOpen((value) => !value)}
      />

      {open ? (
        <div
          id={listId}
          role="group"
          aria-label="City"
          className="mt-2 max-h-64 overflow-y-auto overscroll-contain rounded-md border border-[rgb(var(--ala-border))] bg-[rgb(var(--ala-background))] p-2 shadow-[0_8px_24px_rgb(var(--ala-primary)/0.08)]"
        >
          <ul className="space-y-0.5">
            {cities.map((city) => {
              const checked = selected.includes(city.slug);
              const id = `filter-city-${city.slug}`;
              return (
                <li key={city.slug}>
                  <label
                    htmlFor={id}
                    className="flex cursor-pointer items-center gap-2.5 rounded-md px-2 py-2 transition hover:bg-[rgb(var(--ala-surface))]"
                  >
                    <input
                      id={id}
                      type="checkbox"
                      checked={checked}
                      onChange={() => onChange(toggleCity(selected, city.slug))}
                      className="h-4 w-4 rounded border-[rgb(var(--ala-border))] text-[rgb(var(--ala-secondary))] accent-[rgb(var(--ala-secondary))]"
                    />
                    <span className="min-w-0 flex-1 font-body text-sm text-[rgb(var(--ala-text))]">
                      {formatCityWithState(city.city, city.state)}
                    </span>
                    <span className="font-body text-xs text-[rgb(var(--ala-text-muted))]">
                      {city.count}
                    </span>
                  </label>
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

function NumberOptionsDropdown({
  label,
  emptySummary,
  options,
  selected,
  onChange,
  idPrefix,
}: {
  label: string;
  emptySummary: string;
  options: readonly { value: number; label: string }[];
  selected: number[];
  onChange: (next: number[]) => void;
  idPrefix: string;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const listId = useId();
  const selectedCount = selected.length;
  const summary =
    selectedCount === 0
      ? emptySummary
      : selectedCount === 1
        ? options.find((option) => option.value === selected[0])?.label || '1 selected'
        : `${selectedCount} selected`;

  useDropdownDismiss(open, setOpen, rootRef);

  return (
    <div ref={rootRef} className="relative">
      <FilterDropdownTrigger
        open={open}
        listId={listId}
        summary={summary}
        hasSelection={selectedCount > 0}
        onToggle={() => setOpen((value) => !value)}
      />

      {open ? (
        <div
          id={listId}
          role="group"
          aria-label={label}
          className="mt-2 rounded-md border border-[rgb(var(--ala-border))] bg-[rgb(var(--ala-background))] p-2 shadow-[0_8px_24px_rgb(var(--ala-primary)/0.08)]"
        >
          <ul className="space-y-0.5">
            {options.map((option) => {
              const checked = selected.includes(option.value);
              const id = `${idPrefix}-${option.value}`;
              return (
                <li key={option.value}>
                  <label
                    htmlFor={id}
                    className="flex cursor-pointer items-center gap-2.5 rounded-md px-2 py-2 transition hover:bg-[rgb(var(--ala-surface))]"
                  >
                    <input
                      id={id}
                      type="checkbox"
                      checked={checked}
                      onChange={() => onChange(toggleNumber(selected, option.value))}
                      className="h-4 w-4 rounded border-[rgb(var(--ala-border))] text-[rgb(var(--ala-secondary))] accent-[rgb(var(--ala-secondary))]"
                    />
                    <span className="font-body text-sm text-[rgb(var(--ala-text))]">{option.label}</span>
                  </label>
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

export function CommunitiesFilters({
  filters,
  states,
  cities = [],
  resultCount,
  onChange,
  onReset,
  className = '',
  embedded = false,
}: CommunitiesFiltersProps) {
  return (
    <aside
      className={
        embedded
          ? className
          : `rounded-xl border border-[rgb(var(--ala-border))] bg-[rgb(var(--ala-background))] p-4 shadow-[0_4px_18px_rgb(var(--ala-primary)/0.06)] md:p-5 ${className}`
      }
      aria-label="Community filters"
    >
      <style>{RANGE_SLIDER_CSS}</style>
      {embedded ? null : (
        <div className="mb-5 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 font-heading text-sm font-bold uppercase tracking-wide text-[rgb(var(--ala-text))]">
            <FiltersIcon />
            <span>Filters</span>
          </div>
          <button
            type="button"
            onClick={onReset}
            className="inline-flex items-center gap-1.5 font-heading text-xs uppercase tracking-wide text-[rgb(var(--ala-text-muted))] transition hover:text-[rgb(var(--ala-text))]"
          >
            <ResetIcon />
            Reset
          </button>
        </div>
      )}

      {typeof resultCount === 'number' ? (
        <p className="mb-5 font-body text-sm text-[rgb(var(--ala-text-muted))]">
          <span className="font-semibold text-[rgb(var(--ala-text))]">{resultCount}</span>
          {' '}communit{resultCount === 1 ? 'y' : 'ies'}
        </p>
      ) : null}

      <div className="space-y-5">
        {cities.length > 0 ? (
          <div>
            <p className="mb-3 font-heading text-xs font-semibold uppercase tracking-wide text-[rgb(var(--ala-text-muted))]">
              City
            </p>
            <CityDropdown
              cities={cities}
              selected={filters.cities ?? []}
              onChange={(nextCities) => onChange({ ...filters, cities: nextCities })}
            />
          </div>
        ) : null}

        <div>
          <div className="mb-3 flex items-baseline justify-between gap-3">
            <p className="font-heading text-xs font-semibold uppercase tracking-wide text-[rgb(var(--ala-text-muted))]">
              Price Range
            </p>
            <p className="font-body text-xs text-[rgb(var(--ala-text))]">
              {formatPrice(filters.priceMin)} - {formatPrice(filters.priceMax)}
            </p>
          </div>
          <RangeSlider
            min={FILTER_PRICE_BOUNDS.min}
            max={FILTER_PRICE_BOUNDS.max}
            step={50}
            valueMin={filters.priceMin}
            valueMax={filters.priceMax}
            ariaLabelMin="Minimum price"
            ariaLabelMax="Maximum price"
            onMinChange={(priceMin) => onChange({ ...filters, priceMin })}
            onMaxChange={(priceMax) => onChange({ ...filters, priceMax })}
          />
        </div>

        <div>
          <div className="mb-3 flex items-baseline justify-between gap-3">
            <p className="font-heading text-xs font-semibold uppercase tracking-wide text-[rgb(var(--ala-text-muted))]">
              Square Feet
            </p>
            <p className="font-body text-xs text-[rgb(var(--ala-text))]">
              {filters.sqftMin.toLocaleString()} - {formatSqft(filters.sqftMax)}
            </p>
          </div>
          <RangeSlider
            min={FILTER_SQFT_BOUNDS.min}
            max={FILTER_SQFT_BOUNDS.max}
            step={50}
            valueMin={filters.sqftMin}
            valueMax={filters.sqftMax}
            ariaLabelMin="Minimum square feet"
            ariaLabelMax="Maximum square feet"
            onMinChange={(sqftMin) => onChange({ ...filters, sqftMin })}
            onMaxChange={(sqftMax) => onChange({ ...filters, sqftMax })}
          />
        </div>

        <div>
          <p className="mb-3 font-heading text-xs font-semibold uppercase tracking-wide text-[rgb(var(--ala-text-muted))]">
            What matters to you?
          </p>
          <LifestyleDropdown
            selected={filters.lifestyle ?? []}
            onChange={(lifestyle) => onChange({ ...filters, lifestyle })}
          />
        </div>

        <div>
          <p className="mb-3 font-heading text-xs font-semibold uppercase tracking-wide text-[rgb(var(--ala-text-muted))]">
            Bedroom
          </p>
          <NumberOptionsDropdown
            label="Bedroom"
            emptySummary="Any bedrooms"
            options={BED_OPTIONS}
            selected={filters.bedrooms}
            onChange={(bedrooms) => onChange({ ...filters, bedrooms })}
            idPrefix="filter-bed"
          />
        </div>

        <div>
          <p className="mb-3 font-heading text-xs font-semibold uppercase tracking-wide text-[rgb(var(--ala-text-muted))]">
            Bathroom
          </p>
          <NumberOptionsDropdown
            label="Bathroom"
            emptySummary="Any bathrooms"
            options={BATH_OPTIONS}
            selected={filters.bathrooms}
            onChange={(bathrooms) => onChange({ ...filters, bathrooms })}
            idPrefix="filter-bath"
          />
        </div>

        {states.length > 0 ? (
          <div>
            <p className="mb-3 font-heading text-xs font-semibold uppercase tracking-wide text-[rgb(var(--ala-text-muted))]">
              State
            </p>
            <ul className="space-y-2.5">
              {states.map((state) => {
                const checked = filters.states.includes(state.slug);
                const id = `filter-state-${state.slug}`;
                return (
                  <li key={state.slug}>
                    <label htmlFor={id} className="flex cursor-pointer items-center gap-2.5">
                      <input
                        id={id}
                        type="checkbox"
                        checked={checked}
                        onChange={() =>
                          onChange({
                            ...filters,
                            states: toggleState(filters.states, state.slug),
                          })
                        }
                        className="h-4 w-4 rounded border-[rgb(var(--ala-border))] text-[rgb(var(--ala-secondary))] accent-[rgb(var(--ala-secondary))]"
                      />
                      <span className="font-body text-sm text-[rgb(var(--ala-text))]">{state.name}</span>
                    </label>
                  </li>
                );
              })}
            </ul>
          </div>
        ) : null}
      </div>
    </aside>
  );
}

export default CommunitiesFilters;
